import { makeStyles } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import TableFooter from '@material-ui/core/TableFooter';
import TableRow from '@material-ui/core/TableRow';
import { BigNumber } from 'bignumber.js';
import { MUIDataTableColumn } from 'mui-datatables';
import * as React from 'react';
import { Table } from 'src/components/Table';
import { translate } from 'src/i18n/translate';
import { useFormatAsRestaurantCurrencyNumber } from 'src/services/restaurant/useFormatAsRestaurantCurrencyNumber';
import type { CashRegisterPosBusinessDayReportVm } from 'src/types/CashRegisterPosBusinessDayReportVm';
import { OrderPaymentByWaiter, type PosBusinessDayReportVm } from 'src/types/PosBusinessDayReportVm';
import { isCustomPaymentMethod } from 'src/utils/paymentMethod/isCustomPaymentMethod';
import { sum } from 'src/utils/reduce/sum';
import { toShortId } from 'src/utils/uuid/toShortId';

export function PosBusinessDayOrderPaymentsByWaiterTable({ posBusinessDayReport }: Props): React.ReactElement {
    const classes = useStyles();
    const formatAsCurrencyNumber = useFormatAsRestaurantCurrencyNumber();

    const columns: Array<MUIDataTableColumn> = [
        {
            name: 'orderId',
            options: {
                display: 'excluded',
                filter: false,
            },
        },
        {
            name: 'paymentMethod',
            label: translate('Payment method'),
            options: {
                filter: true,
            },
        },
        {
            name: 'customerName',
            label: translate('Customer'),
            options: {
                filter: false,
            },
        },
        {
            name: 'waiterName',
            label: translate('Waiter'),
            options: {
                filter: false,
            },
        },
        {
            name: 'shortOrderId',
            label: translate('Order Id'),
            options: {
                filter: true,
            },
        },
        {
            name: 'amount',
            label: translate('Sales amount (without tip)'),
            options: {
                filter: true,
            },
        },
        {
            name: 'tip',
            label: translate('Tip'),
            options: {
                filter: false,
            },
        },
        {
            name: 'restaurantServiceAreaName',
            label: translate('Service area'),
            options: {
                filter: false,
            },
        },
    ];

    const getSortedOrderPaymentsByWaiter = () => {
        const sortedOrderPaymentsByWaiter =
            posBusinessDayReport.orderPaymentsByWaiter?.sort((orderPaymentByWaiterA, orderPaymentByWaiterB) =>
                orderPaymentByWaiterA.paymentMethod.localeCompare(orderPaymentByWaiterB.paymentMethod),
            ) ?? [];

        const sortedByDateOrderPaymentsByWaiter = sortedOrderPaymentsByWaiter.reduce<Array<Array<OrderPaymentByWaiter>>>((sortedByDateOrderPaymentsByWaiter, orderPaymentByWaiter) => {
            const paymentMethodHasBeenSortedByDate = sortedByDateOrderPaymentsByWaiter.some((sortedOrderPaymentsByWaiter) => {
                return sortedOrderPaymentsByWaiter.some((sortedOrderPaymentByWaiter) => sortedOrderPaymentByWaiter.paymentMethod === orderPaymentByWaiter.paymentMethod);
            });
            if (paymentMethodHasBeenSortedByDate) return sortedByDateOrderPaymentsByWaiter;

            const filteredOrderPaymentsByWaiter = sortedOrderPaymentsByWaiter.filter((sortedOrderPaymentsByWaiter) => sortedOrderPaymentsByWaiter.paymentMethod === orderPaymentByWaiter.paymentMethod);
            return [
                ...sortedByDateOrderPaymentsByWaiter,
                filteredOrderPaymentsByWaiter.sort((filteredOrderPaymentByWaiterA, filteredOrderPaymentByWaiterB) => {
                    return new Date(filteredOrderPaymentByWaiterA.createdAt).getTime() - new Date(filteredOrderPaymentByWaiterB.createdAt).getTime();
                }),
            ];
        }, []);

        return sortedByDateOrderPaymentsByWaiter.flat();
    };

    const getTotalColumn = (column: 'amount' | 'tip') => {
        return posBusinessDayReport.orderPaymentsByWaiter
            ?.map((orderPaymentByWaiter) => orderPaymentByWaiter[column])
            .reduce(sum, BigNumber(0))
            .toString();
    };

    return (
        <div>
            <Table
                data={
                    getSortedOrderPaymentsByWaiter().map((orderPaymentByWaiter: OrderPaymentByWaiter) => ({
                        orderId: orderPaymentByWaiter.orderId,
                        paymentMethod: isCustomPaymentMethod(orderPaymentByWaiter.paymentMethod)
                            ? orderPaymentByWaiter.paymentMethod
                            : translate(`PaymentMethods.${orderPaymentByWaiter.paymentMethod}`),
                        customerName: orderPaymentByWaiter.customerName,
                        waiterName: orderPaymentByWaiter.waiterName,
                        shortOrderId: toShortId(orderPaymentByWaiter.orderId),
                        amount: formatAsCurrencyNumber(orderPaymentByWaiter.amount),
                        tip: formatAsCurrencyNumber(orderPaymentByWaiter.tip),
                        restaurantServiceAreaName: orderPaymentByWaiter.restaurantServiceAreaName,
                    })) ?? []
                }
                columns={columns}
                options={{
                    responsive: 'standard',
                    selectableRows: 'none',
                    viewColumns: false,
                    print: false,
                    download: true,
                    customTableBodyFooterRender: (options) => {
                        const columnNamesToShowTotal = ['amount', 'tip'];
                        const filteredColumns = options.columns.filter((column) => column.display !== 'excluded');
                        return (
                            <TableFooter className={classes.stickyFooterCell}>
                                <TableRow>
                                    {filteredColumns.map((column, index) => {
                                        if (columnNamesToShowTotal.includes(column.name)) {
                                            return (
                                                <TableCell key={index} className={classes.stickyFooterCell}>
                                                    {formatAsCurrencyNumber(getTotalColumn(column.name))}
                                                </TableCell>
                                            );
                                        }
                                        return <TableCell key={index} className={classes.stickyFooterCell} />;
                                    })}
                                </TableRow>
                            </TableFooter>
                        );
                    },
                }}
            />
        </div>
    );
}

const useStyles = makeStyles((theme) => ({
    stickyFooterCell: {
        backgroundColor: theme.palette.surface.tertiary,
        borderBottom: 'none',
        fontWeight: 900,
        position: 'sticky',
        bottom: 0,
        zIndex: 100,
        '&:hover': {
            backgroundColor: `${theme.palette.surface.tertiary}!important`,
        },
    },
}));

type Props = {
    posBusinessDayReport: PosBusinessDayReportVm | CashRegisterPosBusinessDayReportVm;
};
